import { Col, Form } from "antd"
import { useWatch } from "antd/lib/form/Form"
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ComponentState,
  CurrencyPercentageInput,
  DatePicker,
  Divider,
  Group,
  Icon,
  InputNumber,
  Modal,
  Number,
  RadioButtons,
  SelectInput,
  Skeleton,
  Space,
  Text,
  TextInput,
  notification,
} from "components/common"
import Flex from "components/common/flex"
import { IconText } from "components/custom"
import { genericSelectionType, leadTypes, unitConstructionPhases } from "constants/list"
import {
  useGetLeadDetailsQuery,
  useLazyGetInquiriesWithMortgageAssessmentsQuery,
  useLazyGetMortgageAssessmentQuery,
} from "features/leads/api"
import {
  useGetCountriesQuery,
  useGetProjectsQuery,
  useLazyGetCitiesQuery,
  useLazyGetUnitTypesQuery,
} from "features/shared/api"
import { schemaRules } from "features/shared/utils"
import _ from "lodash"
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import theme from "utils/themeVars"
import mortgageAssessmentDataInitialValues from "./mortgageAssessmentDataInitialValues"
import MortgageCalculationSummary from "./mortgageCalculationSummary"
import mortgageLoanCalculatorValidations from "./mortgageLoanCalculatorValidations"
import { useLazyGetListingInventoryQuery } from "features/inventory/api"
import getPrivilege from "utils/privileges/privileges"
import { useQueryUpdate } from "hooks"
import { useLazyGetCurrentLeadSharingQuery } from "features/clients/detail/api"
import { DATE_FORMAT } from "constants"
import dayjs from "dayjs"
import { INVENTORY_LISTING_PATH } from "constants/app-paths"
import {
  useLazyGetMortgageAssessmentsForInquiryQuery,
  useLazyGetMortgageAssessmentDetailsQuery,
  useGetOccupationTypesQuery,
} from "features/leads/api"
import { configs } from "configs"
import styles from "./index.module.less"
import cx from "clsx"
import { getCurrencySymbol, getLanguagePrefix } from "utils"

const MortgageAssessmentCalculator = forwardRef(
  (
    {
      onShare,
      form,
      addMortgageAssessment,
      editMortgageAssessment,
      isLoading,
      mortgageAssessmentData,
      resetMutation,
      lead,
      resetAll,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const salary = useWatch("salary", form)
    const monthlyObligations = useWatch("totalLiabilities", form)
    const updateMortgageProfile = useWatch("updateMortgageProfile", form)
    const isFirstHome = useWatch("isFirstHome", form)
    const projectId = useWatch("projectId", form)
    const listingId = useWatch("listingId", form)
    const unitPrice = useWatch("unitPrice", form) || 0
    const downPayment = useWatch("downPayment", form) || 0
    const propertyType = useWatch("propertyType", form) || 0
    const inquiry = useWatch("leads", form)
    const assessments = useWatch("assessments", form)
    const city = useWatch("interestedCity", form)
    const unitType = useWatch("unitType", form)

    const [inputType, setInputType] = useState("%")
    const [changeInForm, setChangeInForm] = useState(false)
    const [search, setSearch] = useState(false)
    const rules = schemaRules(mortgageLoanCalculatorValidations({ isFirstHome }))
    const [updateQueryData] = useQueryUpdate()

    const { data: projects = [], isFetching: isFetchingProjects } = useGetProjectsQuery({ status: "on" })
    const [
      getMortgageData,
      { data: mortgageData, isFetching: isFetchingMortgage, isError: isMortgageError, error: mortgageError },
    ] = useLazyGetMortgageAssessmentQuery()
    const [getInquiries, { data: inquiriesData, isFetching: isFetchingInquiries, error: inquiriesError }] =
      useLazyGetInquiriesWithMortgageAssessmentsQuery()
    const [
      getMortgageAssessmentsForLead,
      { data: assessmentsData, isFetching: isFetchingAssessments, error: assessmentsError },
    ] = useLazyGetMortgageAssessmentsForInquiryQuery()
    const [
      getMortgageAssessmentsDetails,
      { data: mortgageDetails, isFetching: isFetchingmortgageDetails, error: mortgageDetailsError },
    ] = useLazyGetMortgageAssessmentDetailsQuery()

    const [getLeadSharingInfo, { data: leadSharedWith, isFetching: isFetchingLeadSharingInfo }] =
      useLazyGetCurrentLeadSharingQuery()
    const { data: { countries = [], nationalities = [] } = {}, isFetching: isFetchingCountries } = useGetCountriesQuery(
      {
        nationalityFlag: true,
      }
    )
    const [getUnitTypes, { data: unitTypes, isFetching: unitTypesLoading }] = useLazyGetUnitTypesQuery()
    const { data: occupationTypes, isFetching: isFetchingOccupationTypes } = useGetOccupationTypesQuery()

    const {
      currentData: leadSalesInfo,
      isFetching: isFetchingLead,
      isError,
      error,
    } = useGetLeadDetailsQuery({ leadId: lead?.id }, { skip: !lead?.id })

    const [getListingInventory, { data: inventoryListingData, isFetching: isfetchinglistingProjects }] =
      useLazyGetListingInventoryQuery()
    const [getCities, { data: cities, isError: isCityError, isFetching: isCitiesFetching }] = useLazyGetCitiesQuery()

    const isOffPlan =
      listingId &&
      inventoryListingData?.find(u => u?.id === listingId)?.projectConstructionStatus === "under-construction"

    const activeContractExists =
      (lead?.interestedIn === "primary" || propertyType === "primary") && leadSalesInfo?.contracts?.length != 0

    const eligibilityExists =
      mortgageData?.eligibleInfoBlock?.[0]?.label === t("Eligible") ||
      mortgageData?.eligibleInfoBlock?.[0]?.label === t("Possibly Eligible") ||
      mortgageAssessmentData?.eligibleInfoBlock?.[0]?.label === t("Eligible") ||
      mortgageAssessmentData?.eligibleInfoBlock?.[0]?.label === t("Possibly Eligible") ||
      mortgageDetails?.eligibleInfoBlock?.[0]?.label === t("Eligible") ||
      mortgageDetails?.eligibleInfoBlock?.[0]?.label === t("Possibly Eligible")

    const currency = useSelector(
      state =>
        state?.app?.user?.info?.Agent?.AgencySettings?.find(setting => setting?.key === "primary_currency")
          ?.currencyCode
    )

    const fetchAndHandleMortgageData = () => {
      getMortgageData({ leadId: lead?.id }, false)
        .unwrap()
        .then(item => {
          getMortgageAssessmentsForLead({ leadId: lead?.id })
        })
        .catch(error => {
          form.setFieldsValue(
            mortgageAssessmentDataInitialValues(
              leadSalesInfo,
              null,
              lead?.Project?.projectId,
              inputType,
              updateMortgageProfile
            )
          )
          notification.error({ message: error?.message })
        })
    }

    const onModalVisible = () => {
      getLeadSharingInfo(lead?.id)
      getInquiries(lead?.clientId)
        .unwrap()
        .catch(error => notification.error({ message: error?.message }))
      fetchAndHandleMortgageData()
    }

    const handleInitialValuesSet = () => {
      const initialValues = mortgageAssessmentDataInitialValues(
        leadSalesInfo,
        mortgageData,
        lead?.Project?.projectId,
        inputType,
        updateMortgageProfile
      )
      form.setFieldsValue(initialValues)
    }

    const adjustDownPayment = val => {
      const obj = {
        value: 0,
        percentage: 0,
        inputType: downPayment?.inputType,
      }
      const dp = val
        ? {
            ...obj,
            ...(inputType == "%"
              ? { value: val * downPayment?.percentage * 0.01, percentage: downPayment?.percentage }
              : { value: downPayment?.value, percentage: (downPayment?.value / val) * 100 }),
          }
        : obj
      form.setFieldsValue({ downPayment: dp })
    }

    useEffect(() => {
      if (!isError || !isMortgageError || mortgageData || leadSalesInfo) {
        handleInitialValuesSet()
        if (mortgageData?.propertyType === "secondary") {
          getUnitTypes({ isFlatHierarchy: true }, true)
          getCities(configs?.countryId)
        }
      }
    }, [leadSalesInfo, isError, isMortgageError, mortgageData])

    useEffect(() => {
      if (projectId) {
        getListingInventory({ projectIds: projectId, activeTab: "all", forUnitsDropDown: true })
      }
    }, [projectId])

    const formSubmit = values => {
      !!!mortgageData && !!!mortgageAssessmentData && !!!mortgageDetails
        ? //POST Api for first time
          addMortgageAssessment({ values, leadId: lead?.id })
            .unwrap()
            .then(item => {
              setChangeInForm(false)
              setSearch(false)
            })
            .catch(error => {
              notification.error({ message: error?.message })
            })
        : //PUT api in edit case
          !search &&
          changeInForm &&
          editMortgageAssessment({
            values,
            inquiryId: lead?.id,
            assessmentId:
              mortgageData?.id ||
              mortgageDetails?.mortgageAssessmentId ||
              mortgageAssessmentData?.mortgageAssessmentRecord?.mortgageAssessmentId ||
              mortgageAssessmentData?.mortgageAssessmentRecord?.id,
          })
            .unwrap()
            .then(item => {
              setChangeInForm(false)
              setSearch(false)
              updateQueryData("getMortgageAssessmentDetails", draft => null)
              getMortgageData({ leadId: inquiry || lead?.id }, false)
            })
            .catch(error => {
              notification.error({ message: error?.message })
            })
    }

    const debouncedSubmit = useCallback(
      _.debounce(() => {
        form.validateFields().then(values => {
          form.submit()
        })
      }, 1000),
      []
    )

    const disableForm = useCallback(() => {
      const isLeadSharedWithMortgage =
        leadSharedWith?.[lead?.id]?.mortgage && Object.keys(leadSharedWith?.[lead?.id]?.mortgage).length > 0
      return !!isLeadSharedWithMortgage
    })

    const clearFormAndCaches = () => {
      resetMutation()
      form?.resetFields()
      updateQueryData("getMortgageAssessment", draft => null, "", { leadId: inquiry })
      updateQueryData("getMortgageAssessmentDetails", draft => null)
    }
    const MortgageCalculatorErrorState = () => {
      return (
        <ComponentState
          description={error?.message}
          status={error?.status}
          btnLoading={isFetchingMortgage}
          onClick={() => fetchAndHandleMortgageData()}
        />
      )
    }
    return (
      <>
        <Modal
          ref={ref}
          title={t("Bank Loan Assessment")}
          onCancel={() => {
            clearFormAndCaches()
            resetAll()
            updateQueryData("getMortgageAssessmentsForInquiry", draft => null, "", { leadId: lead?.id })
          }}
          width={1060}
          footer={
            <Flex gap="8px" justify="space-between">
              <Form
                key={lead?.id}
                form={form}
                layout="vertical"
                onFinish={formSubmit}
                onValuesChange={() => {
                  setChangeInForm(true)
                  setSearch(false)
                }}
              >
                <Form.Item
                  name="updateMortgageProfile"
                  valuePropName="checked"
                  wrapperCol={{ span: 24 }}
                  className="mbe-0"
                >
                  <Checkbox
                    onChange={value => {
                      form.setFieldsValue({ updateMortgageProfile: value.target.checked })
                    }}
                  >
                    {t("Update Clients Bank Loan Profile")}
                  </Checkbox>
                </Form.Item>
              </Form>
              <Flex>
                <Button
                  onClick={() => {
                    resetMutation()
                    form?.resetFields()
                    form.setFieldsValue(
                      mortgageAssessmentDataInitialValues(
                        leadSalesInfo,
                        mortgageData,
                        lead?.Project?.projectId,
                        inputType,
                        updateMortgageProfile
                      )
                    )
                  }}
                >
                  {t("Reset")}
                </Button>
                <Button type="primary" onClick={() => form.submit()} disabled={isfetchinglistingProjects || isLoading}>
                  {t("Calculate")}
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    onShare()
                  }}
                  loading={isFetchingLead || isFetchingMortgage}
                  disabled={
                    !updateMortgageProfile ||
                    disableForm() ||
                    leadSalesInfo?.contracts?.[0]?.ContractType?.key === "self_finance" ||
                    (!mortgageData && !mortgageAssessmentData) ||
                    mortgageAssessmentData?.netLoanCoverage < 70 ||
                    mortgageData?.netLoanCoverage < 70 ||
                    !!!getPrivilege("can_share_lead_with_mortgage", lead?.privileges)
                  }
                >
                  {t("Share with Mortgage")}{" "}
                </Button>
              </Flex>
            </Flex>
          }
          maskClosable={false}
          onModalVisible={onModalVisible}
        >
          {error ? (
            <MortgageCalculatorErrorState />
          ) : (
            <>
              {isFetchingLead ? (
                <Group gap="16px" template={"560px auto"}>
                  <MortgageSummarySkeleton />
                  <MortgageSummarySkeleton />
                </Group>
              ) : (
                <Form
                  key={lead?.id}
                  form={form}
                  layout="vertical"
                  onFinish={formSubmit}
                  onValuesChange={() => {
                    setSearch(false)
                    setChangeInForm(true)
                  }}
                  // disabled={disableForm()}
                  className={cx(styles.mortageModel, "fs12")}
                >
                  <Group template={"1fr 1fr auto auto"} gap="16px" className="mbe-24 flexYend">
                    <Form.Item name="leads" label={t("Leads")} className="mbe-0 flexGrow">
                      <SelectInput
                        key={"lead"}
                        value={mortgageData?.inquiryId}
                        placeholder={t("Select lead")}
                        showArrow
                        options={inquiriesData}
                        onChange={value => {
                          getMortgageAssessmentsForLead({ leadId: value })
                          form.setFieldsValue({
                            assessments: null,
                          })
                        }}
                        allowClear
                        onClear={() =>
                          form.setFieldsValue({
                            leads: null,
                          })
                        }
                      />
                    </Form.Item>
                    <Form.Item label={t("Assessments")} name="assessments" className="mbe-0 flexGrow">
                      <SelectInput
                        key={"assessment"}
                        value={mortgageData?.id}
                        placeholder={t("Select assessment")}
                        inputLoading={isFetchingAssessments}
                        showArrow
                        options={assessmentsData}
                        allowClear
                        onClear={() =>
                          form.setFieldsValue({
                            assessments: null,
                          })
                        }
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      icon={<Icon icon="FiSearch" />}
                      text=""
                      onClick={() => {
                        resetMutation()
                        updateQueryData("getMortgageAssessment", draft => null, "", { leadId: inquiry })
                        updateQueryData("getMortgageAssessmentDetails", draft => null)

                        if (inquiry && assessments) {
                          getMortgageAssessmentsDetails({ id: assessments })
                            .unwrap()
                            .then(item => {
                              setSearch(true)
                              form.setFieldsValue(
                                mortgageAssessmentDataInitialValues(
                                  leadSalesInfo,
                                  { assessmentId: assessments, ...item },
                                  lead?.Project?.projectId,
                                  inputType,
                                  updateMortgageProfile
                                )
                              )
                            })
                            .catch(error => {})
                        } else if (inquiry) {
                          getMortgageData({ leadId: inquiry }, false)
                          setSearch(true)
                        }
                      }}
                      style={{ height: 40, width: 40 }}
                    />

                    <Button
                      type="link"
                      danger
                      text={t("Clear")}
                      onClick={() => {
                        clearFormAndCaches()
                        form.setFieldsValue({ updateMortgageProfile: true })
                        form.setFieldsValue(
                          mortgageAssessmentDataInitialValues(
                            leadSalesInfo,
                            null,
                            lead?.Project?.projectId,
                            inputType,
                            updateMortgageProfile
                          )
                        )
                        updateQueryData("getMortgageAssessmentsForInquiry", draft => null)
                      }}
                      style={{ height: 40, width: 40 }}
                      disabled={!(assessments != null || inquiry != null)}
                    />
                  </Group>
                  <Group gap="40px" template={"540px auto"}>
                    <div>
                      <Text className="semiBold">{t("Client Profile")}</Text>
                      <Divider style={{ marginBlock: "12px" }} />
                      <Group gap="0 16px" template={"1fr 1fr"}>
                        <Form.Item label={t("Monthly Net Salary")} name="salary" rules={rules} required>
                          <TextInput
                            placeholder={t("Enter Monthly Salary")}
                            suffixText={<Number compact="intl" value={salary} />}
                            style={{ width: "100%" }}
                            addonAfter={getCurrencySymbol()}
                            maxLength={15}
                          />
                        </Form.Item>

                        <Form.Item
                          label={
                            <Flex align="center" gap="4px">
                              <Text type="secondary">{t("Total Monthly Obligations")}</Text>

                              <Icon
                                hasBackground
                                icon={"BsInfo"}
                                color="#707070"
                                size="16px"
                                style={{
                                  backgroundColor: "#E6E6E6",
                                  borderRadius: "1rem",
                                  "--icon-color": "#707070",
                                  margin: -8,
                                }}
                                tooltipText={t(
                                  "Total monthly obligations includes car loan, personal loan or credit cards"
                                )}
                              />
                            </Flex>
                          }
                          name="totalLiabilities"
                          rules={rules}
                          required
                        >
                          <TextInput
                            placeholder={t("Enter Monthly Obligations")}
                            suffixText={<Number compact="intl" value={monthlyObligations} />}
                            style={{ width: "100%" }}
                            addonAfter={getCurrencySymbol()}
                            maxLength={15}
                          />
                        </Form.Item>

                        <Form.Item label={t("Nationality")} name="nationalityId" rules={rules} required>
                          <SelectInput
                            showSearch
                            placeholder={t("Select nationality")}
                            options={nationalities}
                            inputLoading={isFetchingCountries}
                          />
                        </Form.Item>

                        <Form.Item label={t("Is this your first home?")} name="isFirstHome" rules={rules} required>
                          <RadioButtons options={genericSelectionType()} />
                        </Form.Item>
                        <Form.Item label={t("Occupation Type")} name="occupationType" rules={rules}>
                          <SelectInput
                            showSearch={false}
                            placeholder={t("Select occupation type")}
                            options={occupationTypes}
                          />
                        </Form.Item>
                        <Form.Item
                          name={"dateOfBirth"}
                          label={t("Date of Birth")}
                          rules={rules.concat({
                            validator: async (_, value) => {
                              if (!value) {
                                return Promise.resolve()
                              }
                              if (dayjs().diff(value, "years") < 17) {
                                return Promise.reject(t("Age must be greater than 16 years old"))
                              }
                              return Promise.resolve()
                            },
                          })}
                        >
                          <DatePicker
                            allowClear
                            showTime={false}
                            format={DATE_FORMAT}
                            defaultPickerValue={dayjs().subtract(17, "years").startOf("day")}
                            disabledDate={current => current && current >= dayjs().startOf("day")}
                          />
                        </Form.Item>
                      </Group>

                      <Text className="semiBold">{t("Interested Project/Unit")}</Text>
                      <Divider style={{ marginBlock: "12px" }} />
                      <Group gap="0 16px" template={"1fr 1fr"}>
                        {lead?.interestedIn !== "primary" && (
                          <Form.Item label={t("Property Type")} name="propertyType" rules={rules} required>
                            <RadioButtons
                              options={
                                lead?.interestedIn === "mortgage"
                                  ? leadTypes().filter(
                                      item => item.value != "mortgage" && item.value != "advertisement"
                                    )
                                  : leadTypes().filter(item => item.value === "secondary")
                              }
                              getValue={obj => obj.value}
                              onChange={option => {
                                form.setFieldsValue({
                                  downPayment: {
                                    value: 0,
                                    percentage: 0,
                                    inputType: downPayment?.inputType,
                                  },
                                  preferredUnitConstructionPhase: null,
                                  unitType: null,
                                  interestedCity: null,
                                })
                                if (option.target.value === "primary") {
                                  form.setFieldsValue({
                                    projectId: lead?.projectId?.toString() || projectId?.toString(),
                                    listingId: lead?.listingId || null,
                                    unitPrice: lead?.unitPrice,
                                  })
                                } else {
                                  getUnitTypes({ isFlatHierarchy: true }, true)
                                  getCities(configs?.countryId)
                                }
                              }}
                            />
                          </Form.Item>
                        )}
                        {((lead?.interestedIn !== "primary" && propertyType === "primary") ||
                          lead?.interestedIn === "primary") && (
                          <>
                            <Form.Item name="projectId" label={t("Select Project")} required rules={rules}>
                              <SelectInput
                                options={projects}
                                placeholder={t("Select project")}
                                onChange={() => {
                                  form.setFieldsValue({
                                    unitPrice: null,
                                    downPayment: null,
                                    listingId: null,
                                  })
                                }}
                                disabled={activeContractExists || lead?.canShareWithMortgage || disableForm()}
                              />
                            </Form.Item>

                            <Form.Item label={t("Unit")} name="listingId" rules={rules} required>
                              <SelectInput
                                options={inventoryListingData}
                                placeholder={t("Select Unit")}
                                inputLoading={isfetchinglistingProjects}
                                onChange={(value, option) => {
                                  form.setFieldsValue({
                                    unitPrice: +option?.price?.toString(),
                                  })
                                  adjustDownPayment(+option?.price)
                                }}
                                disabled={
                                  isfetchinglistingProjects ||
                                  activeContractExists ||
                                  lead?.canShareWithMortgage ||
                                  !!!projectId ||
                                  disableForm()
                                }
                              />
                            </Form.Item>
                            {inventoryListingData && inventoryListingData?.length === 0 && (
                              <Alert
                                className="span-all mbe-32"
                                type="error"
                                message={t("The selected project has no units available")}
                                icon={<Icon icon="RiErrorWarningLine" />}
                                showIcon
                              />
                            )}
                          </>
                        )}

                        {propertyType === "secondary" && (
                          <>
                            <Form.Item
                              label={t("Preferred Unit Construction Phase")}
                              name="preferredUnitConstructionPhase"
                              rules={rules}
                              required
                            >
                              <RadioButtons options={unitConstructionPhases()} />
                            </Form.Item>
                            <Form.Item label={t("Unit Type")} name="unitType" rules={rules} required>
                              <SelectInput showSearch placeholder={t("Select Unit Type")} options={unitTypes} />
                            </Form.Item>
                            <Form.Item label={t("Interested City")} name="interestedCity" rules={rules} required>
                              <SelectInput showSearch placeholder={t("Select Interested City")} options={cities} />
                            </Form.Item>
                          </>
                        )}
                        {(propertyType || lead?.interestedIn === "primary") && (
                          <>
                            {" "}
                            <Form.Item label={t("Unit Price")} name="unitPrice" rules={rules} required>
                              <InputNumber
                                min={0}
                                suffix={
                                  <Number
                                    compact="intl"
                                    color={theme["segmented-label-color"]}
                                    value={unitPrice || "-"}
                                  />
                                }
                                onChange={val => adjustDownPayment(val)}
                                style={{ width: "100%" }}
                                addonAfter={getCurrencySymbol()}
                                disabled={activeContractExists || propertyType != "secondary"}
                              />
                            </Form.Item>
                            <Col>
                              <Form.Item
                                name="downPayment"
                                label={t("Cash Contribution")}
                                rules={rules}
                                help={
                                  <IconText
                                    iconProps={{ color: theme["gray900"], size: "1em" }}
                                    textType="secondary"
                                    icon="MdInfoOutline"
                                    title={t(`Remaining amount is {{remainingAmount}}`, {
                                      remainingAmount: unitPrice - unitPrice * (downPayment?.percentage / 100) || 0,
                                    })}
                                  />
                                }
                              >
                                <CurrencyPercentageInput
                                  placeholder={t("Enter Cash Contribution")}
                                  field={downPayment}
                                  totalPrice={unitPrice}
                                  onChange={option => {
                                    setInputType(option?.inputType)
                                    form.setFieldsValue({ downPayment: option })
                                  }}
                                  maxAmount={unitPrice}
                                  maxPercentage={100}
                                  subtext
                                  disabled={activeContractExists}
                                />
                              </Form.Item>
                            </Col>
                          </>
                        )}
                      </Group>
                    </div>
                    <Card
                      variant="fill"
                      style={{
                        "--bg-color":
                          mortgageAssessmentData?.mortgageSummaryColors?.loanbreakDownColor || theme["gray100"],
                      }}
                      title={
                        <Flex justify="space-between">
                          {t("Bank Loan Summary")}
                          {eligibilityExists && (
                            <Text
                              type="primary"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                const range = [
                                  0,
                                  mortgageDetails?.snapshot?.netLoanAmount ||
                                    mortgageData?.snapshot?.netLoanAmount ||
                                    mortgageAssessmentData?.netLoanAmount,
                                ]
                                window.open(
                                  `${getLanguagePrefix()}${INVENTORY_LISTING_PATH}?priceRange=${range}${
                                    propertyType !== "secondary"
                                      ? `&projectIds=${projectId}`
                                      : `&typeIds=${unitType}&cityIds=${city}`
                                  }`,
                                  "_blank"
                                )
                              }}
                            >
                              {t("Recommended Units")}
                              <Icon icon="FiArrowUpRight" />
                            </Text>
                          )}
                        </Flex>
                      }
                      headStyle={{
                        backgroundColor: "transparent",
                        borderBottom: "none",
                      }}
                      bodyStyle={{
                        padding: "0px 16px 16px",
                      }}
                    >
                      {isLoading ? (
                        <MortgageSummarySkeleton />
                      ) : !mortgageAssessmentData && !mortgageData && !mortgageDetails ? (
                        <ComponentState
                          description={t("Please provide the details needed to assess the client's loan eligibility")}
                          type="empty"
                        />
                      ) : (
                        <MortgageCalculationSummary
                          form={form}
                          {...{
                            mortgageAssessmentData: mortgageDetails || mortgageAssessmentData || mortgageData,
                            currency,
                            rules,
                            debouncedSubmit,
                          }}
                        />
                      )}
                    </Card>
                  </Group>
                </Form>
              )}
            </>
          )}
        </Modal>
      </>
    )
  }
)

export default MortgageAssessmentCalculator

const MortgageSummarySkeleton = () => {
  const skeletonItems = Array.from({ length: 4 })

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {skeletonItems.map((_, index) => (
          <Skeleton key={`top-${index}`} width={250} height={26} active />
        ))}
      </Space>

      <Space size={20} style={{ marginTop: 24 }} align="center">
        <Skeleton key="middle-1" active width={120} height={26} />
        <Skeleton key="middle-2" active width={120} height={26} />
      </Space>

      <Divider style={{ marginTop: 24 }} />

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {skeletonItems.map((_, index) => (
          <Skeleton key={`bottom-${index}`} width={250} height={26} active />
        ))}
      </div>
    </div>
  )
}
